import React, { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2, Search, Upload, Trash2, ImageIcon, Layers, CheckCircle2, AlertTriangle, Crop,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  adminMarketList, adminMarketImageStats, adminMarketSetImage,
  adminMarketRemoveImage, adminMarketGroupImage, apiErr,
} from "@/lib/api";
import ImageCropDialog from "./ImageCropDialog";

const PAGE = 60;
const isReal = (img) => !!img && !String(img).endsWith(".svg");

export default function MarketImageManager() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [visible, setVisible] = useState(PAGE);
  const [crop, setCrop] = useState(null); // { file, target: {kind, slug?, group_key?, label} }
  const [bulkOnlyPlaceholder, setBulkOnlyPlaceholder] = useState(true);
  const [removeTarget, setRemoveTarget] = useState(null);
  const fileRef = useRef(null);
  const bulkFileRef = useRef(null);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["admin", "market"],
    queryFn: adminMarketList,
  });
  const { data: stats } = useQuery({
    queryKey: ["admin", "market-image-stats"],
    queryFn: adminMarketImageStats,
  });

  const groups = useMemo(() => {
    const seen = new Map();
    products.forEach((p) => { if (!seen.has(p.group_key)) seen.set(p.group_key, p.group_label); });
    return [...seen.entries()].map(([key, label]) => ({ key, label }));
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    return products.filter((p) => {
      if (group !== "all" && p.group_key !== group) return false;
      if (onlyMissing && isReal(p.image)) return false;
      if (q && !p.name.toLocaleLowerCase("tr").includes(q) &&
          !(p.subcategory || "").toLocaleLowerCase("tr").includes(q)) return false;
      return true;
    });
  }, [products, group, search, onlyMissing]);

  const visibleRows = filtered.slice(0, visible);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "market"] });
    qc.invalidateQueries({ queryKey: ["admin", "market-image-stats"] });
    qc.invalidateQueries({ queryKey: ["market-products"] });
  };

  // --- per-product upload (yeni dosya seç) ---
  const pickFor = (p) => {
    setCrop({ pending: { kind: "product", slug: p.slug, label: p.name } });
    fileRef.current.value = "";
    fileRef.current.click();
  };
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file || !crop?.pending) return;
    setCrop((c) => ({ file, target: c.pending }));
  };

  // --- mevcut görseli yeniden hizala/boyutlandır (indirip yüklemeye gerek yok) ---
  const editExisting = (p) => {
    setCrop({ srcUrl: p.image, target: { kind: "product", slug: p.slug, label: p.name } });
  };

  // --- bulk group upload ---
  const pickBulk = () => {
    if (group === "all") {
      toast.error("Toplu atama için önce bir grup seçin");
      return;
    }
    const g = groups.find((x) => x.key === group);
    setCrop({ pending: { kind: "group", group_key: group, label: g?.label || group } });
    bulkFileRef.current.value = "";
    bulkFileRef.current.click();
  };
  const onBulkFile = (e) => {
    const file = e.target.files?.[0];
    if (!file || !crop?.pending) return;
    setCrop((c) => ({ file, target: c.pending }));
  };

  const doUpload = async (croppedFile) => {
    const t = crop.target;
    try {
      if (t.kind === "product") {
        await adminMarketSetImage(t.slug, croppedFile);
        toast.success("Görsel yüklendi");
      } else {
        const res = await adminMarketGroupImage(croppedFile, t.group_key, bulkOnlyPlaceholder);
        toast.success(`${res.updated} ürüne uygulandı`);
      }
      invalidate();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setCrop(null);
    }
  };

  const removeImage = async () => {
    const slug = removeTarget.slug;
    setRemoveTarget(null);
    try {
      await adminMarketRemoveImage(slug);
      toast.success("Görsel kaldırıldı (placeholder'a döndü)");
      invalidate();
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  return (
    <div data-testid="market-image-manager" className="pb-10">
      {/* İlerleme paneli */}
      <div data-testid="market-image-progress" className="mb-5 rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Market Görsel Yönetimi</h3>
            <p className="text-sm text-gray-500">Gerçek paket fotoğrafları ile kataloğu zenginleştirin.</p>
          </div>
          <div className="text-right">
            <div className="font-mono text-3xl font-bold text-[#9a5e30]" data-testid="market-image-percent">
              {stats ? `%${stats.percent}` : "—"}
            </div>
            <div className="text-xs text-gray-500">tamamlandı</div>
          </div>
        </div>
        {stats && (
          <>
            <Progress value={stats.percent} className="mt-3 h-2" />
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
              <span>Toplam: <b className="text-gray-700">{stats.total}</b></span>
              <span className="text-emerald-600">Gerçek Fotoğraf: <b>{stats.real}</b></span>
              <span className="text-amber-600">Placeholder: <b>{stats.placeholder}</b></span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {stats.groups.map((g) => {
                const pct = g.total ? Math.round((g.real / g.total) * 100) : 0;
                return (
                  <button
                    key={g.group_key}
                    onClick={() => { setGroup(g.group_key); setVisible(PAGE); }}
                    data-testid={`market-image-group-stat-${g.group_key}`}
                    className={`rounded-lg border p-2 text-left transition-colors hover:border-[#C57B43] ${
                      group === g.group_key ? "border-[#C57B43] bg-[#C57B43]/5" : "border-gray-100"
                    }`}
                  >
                    <div className="truncate text-[11px] font-medium text-gray-700">{g.group_label}</div>
                    <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500">
                      <span>{g.real}/{g.total}</span>
                      <span className={pct === 100 ? "text-emerald-600" : "text-amber-600"}>%{pct}</span>
                    </div>
                    <Progress value={pct} className="mt-1 h-1" />
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Filtreler */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            data-testid="market-image-search"
            placeholder="Ürün ara…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisible(PAGE); }}
            className="pl-9"
          />
        </div>
        <Select value={group} onValueChange={(v) => { setGroup(v); setVisible(PAGE); }}>
          <SelectTrigger className="w-[220px]" data-testid="market-image-group">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Gruplar</SelectItem>
            {groups.map((g) => (
              <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <Switch checked={onlyMissing} onCheckedChange={(v) => { setOnlyMissing(v); setVisible(PAGE); }} data-testid="market-image-only-missing" />
          Sadece eksikler
        </label>
      </div>

      {/* Toplu görsel atama (kategori kapağı) */}
      <div data-testid="market-image-bulk-panel" className="mb-5 rounded-lg border border-[#C57B43]/40 bg-[#C57B43]/[0.06] p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-[#9a5e30]">
          <Layers className="h-4 w-4" /> Kategori Kapağı (Toplu Atama)
          <span className="font-normal text-gray-500">
            — {group === "all" ? "önce bir grup seçin" : `seçili gruba tek görsel uygula`}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            data-testid="market-image-bulk-apply"
            onClick={pickBulk}
            disabled={group === "all"}
            className="bg-[#C57B43] hover:bg-[#b06d39]"
          >
            <Upload className="mr-1.5 h-4 w-4" /> Kapak Görseli Seç
          </Button>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <Switch checked={bulkOnlyPlaceholder} onCheckedChange={setBulkOnlyPlaceholder} data-testid="market-image-bulk-only-placeholder" />
            Sadece placeholder'lara uygula (gerçek fotoğrafları koru)
          </label>
        </div>
      </div>

      {/* gizli dosya inputları */}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="market-image-file-input" />
      <input ref={bulkFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onBulkFile} data-testid="market-image-bulk-file-input" />

      {isLoading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : (
        <>
          <p className="mb-2 text-xs text-gray-400" data-testid="market-image-count">
            {filtered.length} ürün · {Math.min(visible, filtered.length)} gösteriliyor
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {visibleRows.map((p) => {
              const real = isReal(p.image);
              return (
                <div
                  key={p.slug}
                  data-testid={`market-image-row-${p.slug}`}
                  className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
                >
                  <div className="relative aspect-[5/6] bg-[#120D0A]">
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover" loading="lazy" />
                    <span
                      className={`absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        real ? "bg-emerald-500/90 text-white" : "bg-amber-400/90 text-charcoal"
                      }`}
                    >
                      {real ? <CheckCircle2 className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                      {real ? "Gerçek" : "Placeholder"}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-2.5">
                    <div className="line-clamp-2 text-xs font-medium text-gray-800">{p.name}</div>
                    <div className="mt-0.5 text-[10px] text-gray-400">{p.subcategory}</div>
                    <div className="mt-auto flex items-center gap-1.5 pt-2">
                      {real ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 flex-1 px-2 text-xs"
                            onClick={() => editExisting(p)}
                            data-testid={`market-image-edit-${p.slug}`}
                            title="Mevcut görseli yeniden hizala / boyutlandır"
                          >
                            <Crop className="mr-1 h-3.5 w-3.5" /> Hizala
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-xs"
                            onClick={() => pickFor(p)}
                            data-testid={`market-image-upload-${p.slug}`}
                            title="Yeni dosya yükle"
                          >
                            <Upload className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-xs text-red-500 hover:bg-red-50 hover:text-red-600"
                            onClick={() => setRemoveTarget(p)}
                            data-testid={`market-image-remove-${p.slug}`}
                            title="Görseli kaldır"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 flex-1 px-2 text-xs"
                          onClick={() => pickFor(p)}
                          data-testid={`market-image-upload-${p.slug}`}
                        >
                          <Upload className="mr-1 h-3.5 w-3.5" /> Yükle
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="py-12 text-center text-sm text-gray-400">Ürün bulunamadı.</div>
          )}
          {visible < filtered.length && (
            <div className="mt-5 text-center">
              <Button
                data-testid="market-image-load-more"
                variant="outline"
                onClick={() => setVisible((v) => v + PAGE)}
              >
                Daha Fazla Göster ({filtered.length - visible})
              </Button>
            </div>
          )}
        </>
      )}

      {/* Kırpma / hizalama diyaloğu (yeni dosya veya mevcut görsel) */}
      {(crop?.file || crop?.srcUrl) && (
        <ImageCropDialog
          open
          file={crop.file}
          srcUrl={crop.srcUrl}
          title={crop.target.kind === "group"
            ? `"${crop.target.label}" grubuna kapak`
            : crop.target.label}
          onCancel={() => setCrop(null)}
          onConfirm={doUpload}
        />
      )}

      {/* Kaldırma onayı */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="market-image-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Görseli kaldır
            </AlertDialogTitle>
            <AlertDialogDescription>
              <b>{removeTarget?.name}</b> ürününün gerçek fotoğrafı kaldırılıp grup placeholder'ına dönecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="market-image-remove-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={removeImage} data-testid="market-image-remove-confirm" className="bg-red-500 hover:bg-red-600">
              Kaldır
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
